import { CSSProperties, ResponsiveStyleValue } from "theme-ui";
import { TextProps } from "./GetUpToSection";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import HeroButtons from "../Buttons/HeroButtons";
import SlantSection from "../Slant/Slant";
import { ButtonProps } from "../../../custom";

interface Props {
  backgroundColor: string;
  backgroundImage?: IGatsbyImageData;
  image?: IGatsbyImageData;
  reverse?: boolean;
  logo?: IGatsbyImageData;
  buttonFlexDirection?: string;
  position?: "top" | "bottom";
  imagePosition?: string | number | (string | number)[];
  heroWidth?: string | number | (string | number)[];
  contentWidth?: string | number | (string | number)[];
  backgroundImageStyle?: CSSProperties;
  heroImage: IGatsbyImageData;
  subText?: TextProps;
  subHeader?: TextProps;
  linkText?: TextProps;
  heading: TextProps;
  alignCenter?: boolean;
  overflow?: boolean;
  justifyCenter?: boolean;
  imageMarginTop?: boolean;
  button1: ButtonProps;
  button2: ButtonProps;
  className?: string;
  imageMaxWidth?: string | number | (string | number)[];
  customButton?: React.ReactNode;
  children?: React.ReactNode;
  showChildren?: boolean;
  fullWidth?: boolean;
}

const HeroSection = ({
  heading,
  className,
  subText,
  heroImage,
  justifyCenter,
  image,
  linkText,
  position,
  imagePosition,
  heroWidth,
  backgroundImageStyle,
  buttonFlexDirection,
  imageMarginTop,
  contentWidth = "50%",
  overflow = true,
  reverse,
  logo,
  button1,
  button2,
  alignCenter,
  backgroundColor,
  backgroundImage,
  imageMaxWidth,
  subHeader,
  customButton,
  children,
  showChildren,
  fullWidth,
}: Props) => {
  const leftRight = reverse ? { left: 0 } : { right: 0 };
  const topBottom = imagePosition
    ? imagePosition
    : position === "bottom"
    ? { bottom: [0, null, null, -100] }
    : { top: 0 };

  return (
    <SlantSection
      variant="right"
      background={backgroundColor}
      sx={{
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        zIndex: 4,
      }}
    >
      <div
        sx={{
          display: "flex",
          position: "relative",
          padding: 0,
          px: "0!important",
          pb: [backgroundImage ? 100 : 0, null, null, 100, 100],
          width: "100%",
          maxWidth: fullWidth ? "100%" : 1920,
          zIndex: 1,
          flexWrap: "wrap",
          overflow: "hidden",
          justifyContent: "center",
        }}
      >
        <SlantSection
          variant="right"
          background={backgroundColor}
          className={className}
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            overflow: "hidden",
            top: [0, null, null, 100],
            pb: 3,
            mt: [100, null, null, 0],
            pt: [4, null, null, 0],
            mb: [0, null, null, 0],
            zIndex: [4, null, null, 0],
            flexDirection: reverse ? "row-reverse" : "row",
          }}
        >
          <div
            sx={{
              width: ["100%", null, null, showChildren ? "100%" : contentWidth],
            }}
          >
            {backgroundImage && (
              <GatsbyImage
                image={backgroundImage}
                alt="Main hero"
                sx={{
                  ...leftRight,
                  // @ts-ignore
                  ...topBottom,
                  display: ["block"],
                  position: ["absolute"],
                  opacity: [0.4, null, null, 1],
                  width: ["100vw", null, null, heroWidth ? heroWidth : "80%"],
                  height: ["100%", null, null, "auto"],
                  zIndex: 2,
                  ...backgroundImageStyle,
                }}
              />
            )}
            {showChildren ? (
              <div
                sx={{
                  position: "relative",
                  width: "100%",
                  zIndex: 9,
                }}
              >
                {children}
              </div>
            ) : (
              <div
                data-sal={"slide-right"}
                data-sal-easing="easeOutQuart"
                data-sal-duration={1000}
                data-sal-delay="100"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: alignCenter ? "center" : "flex-start",
                  justifyContent: justifyCenter ? "center" : "flex-start",
                  zIndex: 9,
                  position: "relative",
                  color: "white",
                  mt: [backgroundImage ? "5vw" : 0, null, null, "4vw"],
                  mb: [backgroundImage ? "10vw" : "0", null, "6vw"],
                  // width: ["100%", null, null, "50%"],
                  pl: [3, null, null, reverse ? 0 : 5],
                  pr: [3, null, null, reverse ? 5 : 0],
                }}
              >
                {logo && (
                  <GatsbyImage
                    image={logo}
                    alt="Logo"
                    objectFit="contain"
                    sx={{
                      maxHeight: [100, null, null, 250, 300],
                      mb: [3],
                      alignSelf: ["center", null, null, "center"],
                    }}
                  />
                )}
                <h1
                  sx={{
                    fontStyle: "italic",
                    my: 0,
                    color: [
                      heading.colorMobile ? heading.colorMobile : heading.color,
                      null,
                      null,
                      heading.color,
                    ],
                    textAlign: alignCenter ? "center" : "left",
                    sup: {
                      fontSize: 5,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: heading.text }}
                />
                {subHeader && (
                  <h2
                    sx={{
                      color: subHeader.color,
                      // pb: 0,
                    }}
                    dangerouslySetInnerHTML={{ __html: subHeader.text }}
                  />
                )}
                {subText && (
                  <p
                    sx={{
                      color: subText.color,
                      maxWidth: 600,
                    }}
                    dangerouslySetInnerHTML={{ __html: subText.text }}
                  />
                )}
                {!customButton && button1 && (
                  <HeroButtons
                    buttonFlexDirection={buttonFlexDirection || "row"}
                    button1={button1}
                    button2={button2}
                  />
                )}
                {customButton && customButton}
                {linkText && (
                  <p
                    sx={{
                      color: linkText.color,
                      mt: [-30, null, 0],
                      pb: [0, null, null, 4, 0],
                      fontWeight: 600,
                      a: {
                        fontSize: [18, null, null, 22],
                        color: "white",
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: linkText.text }}
                  />
                )}
                {image && <GatsbyImage image={image} alt="graphic" />}
              </div>
            )}
          </div>

          {overflow == false ? (
            <div
              data-sal={"fade-in"}
              data-sal-easing="easeOutQuart"
              data-sal-duration={1000}
              data-sal-delay="100"
              sx={{
                bg: [backgroundColor, null, null, "transparent"],
                pt: [0, null, null, 0],
                width: "100%",
                zIndex: 2,
                display: "flex",
                justifyContent: [
                  alignCenter ? "center" : "flex-start",
                  null,
                  null,
                  "flex-start",
                ],
              }}
            >
              {!backgroundImage && (
                <GatsbyImage
                  className="hero-image"
                  image={heroImage}
                  alt="Main hero"
                  sx={{
                    ...leftRight,
                    // @ts-ignore
                    ...topBottom,
                    objectFit: "contain",
                    display: ["block"],
                    position: ["relative", null, null, "absolute"],
                    width: ["100%", null, null, heroWidth ? heroWidth : "55%"],
                    mt: [imageMarginTop ? 70 : 0, null, null, 0],
                    maxWidth: imageMaxWidth ? imageMaxWidth : [1100],
                    zIndex: 2,
                  }}
                />
              )}
            </div>
          ) : (
            <></>
          )}
        </SlantSection>
        {!showChildren && !backgroundImage && overflow && (
          <div
            sx={{
              position: "relative",
              pt: [0, null, null, 0],
              width: "100%",
              zIndex: 8,
              mt: [0, null, null, 0],
              height: [400, 600, null, "100%"],
              background: [
                `url(${heroImage.images.fallback?.src}) ${backgroundColor}`,
                null,
                null,
                "transparent",
              ],
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: ["center 30px", "top 30px", "0px 0px"],
            }}
          >
            <div
              data-sal={"fade-in"}
              data-sal-easing="easeOutQuart"
              data-sal-duration={1000}
              data-sal-delay="100"
              sx={{
                display: "flex",
                justifyContent: [
                  alignCenter ? "center" : "flex-start",
                  null,
                  null,
                  "flex-start",
                ],
              }}
            >
              {!backgroundImage && (
                <GatsbyImage
                  className="hero-image"
                  image={heroImage}
                  alt="Main hero"
                  sx={{
                    ...leftRight,
                    // @ts-ignore
                    ...topBottom,
                    objectFit: "contain",
                    display: ["none", null, null, "block"],
                    position: ["relative", null, null, "absolute"],
                    width: ["100%", null, null, heroWidth ? heroWidth : "55%"],
                    mt: [imageMarginTop ? 70 : 0, null, null, 0],
                    maxWidth: imageMaxWidth ? imageMaxWidth : [1100],
                    zIndex: 2,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </SlantSection>
  );
};

export default HeroSection;
